import {
  trackError,
  trackFeatureUsage,
  trackLogin,
  trackLogout,
} from "../utils/analytics";
import { trackRegisterStep } from "./funnelAnalytics";

/**
 * Embudo de autenticacion y alta de cuenta.
 *
 * Es el unico embudo de la app donde el abandono cuesta un cliente, y hasta hoy
 * no emitia nada: `trackLogin` y `trackLogout` existian en la fachada sin un
 * solo llamador.
 *
 * Regla de privacidad de este archivo: aqui pasan correos, usuarios,
 * contrasenas, nombres y RFC. NADA de eso sale. Solo viajan el metodo, el
 * codigo de error de Firebase/HTTP y el motivo de bloqueo, que son
 * enumeraciones cerradas.
 */

export type AuthMethod = "password" | "google";

/** Por que se cayo un intento de entrada, antes de llegar a Firebase. */
export type LoginBlockReason =
  | "username_not_found"
  | "user_data_missing"
  | "location_permission_denied"
  | "location_permission_cancelled"
  | "access_denied"
  | "company_suspended"
  | "session_already_active"
  | "unknown";

const FEATURE = "auth";

/**
 * Entrada correcta. Emite el evento `login` estandar de GA4.
 *
 * No lleva rol ni empresa a proposito: `identifyPostHog` ya los fija como
 * propiedades de persona y `setAnalyticsUserProperties` como super
 * propiedades, asi que repetirlos por evento solo agrega ruido.
 */
export const trackLoginSucceeded = (method: AuthMethod): void => {
  void trackLogin(method);
};

/**
 * Entrada fallida con el codigo de error, nunca el mensaje.
 *
 * El mensaje esta traducido y puede interpolar el nombre del patio, el horario
 * o el dispositivo de la sesion abierta; el codigo (`auth/wrong-password`,
 * `auth/too-many-requests`) es una enumeracion cerrada de Firebase.
 */
export const trackLoginFailed = (
  method: AuthMethod,
  errorCode: string,
  httpStatus?: number,
): void => {
  void trackError("login_failed", errorCode, "auth");
  void trackFeatureUsage(FEATURE, "login_failed", {
    method,
    error_code: errorCode,
    http_status: httpStatus ?? 0,
  });
};

/**
 * Entrada rechazada por una regla de negocio, no por credenciales malas.
 *
 * Separar esto de `login_failed` es el punto: un pico de
 * `access_denied` o `session_already_active` es un problema de operacion
 * (geocerca mal puesta, turno mal configurado, caseta compartida), no de que
 * la gente olvide su contrasena.
 */
export const trackLoginBlocked = (
  reason: LoginBlockReason,
  detail?: string,
): void => {
  void trackFeatureUsage(FEATURE, "login_blocked", {
    reason,
    detail: detail ?? "",
  });
};

export const trackLogoutCompleted = (): void => {
  void trackLogout();
};

/**
 * Peticion de restablecer contrasena.
 *
 * `rateLimited` viene del 429 del backend. El resto de fallos NO se reporta a
 * proposito: el backend responde igual exista o no la cuenta, y emitir un
 * evento de fallo ahi reintroduciria por analitica la enumeracion de usuarios
 * que el backend evita.
 */
export const trackPasswordResetRequested = (rateLimited: boolean): void => {
  void trackFeatureUsage(FEATURE, "password_reset_requested", {
    rate_limited: rateLimited,
  });
};

/** Paso del alta completado (1 datos, 2 contrasena de Google, 3 empresa). */
export const trackRegisterStepCompleted = (
  step: number,
  method: AuthMethod | "email",
  hasPlan: boolean,
): void => {
  void trackFeatureUsage(FEATURE, "register_step_completed", {
    step,
    method,
    has_plan: hasPlan,
  });  // Embudo de PostHog (CC-CONSOLE#1538): mismo paso con nombre estable.
  trackRegisterStep(step === 1 ? "account_created" : "company_created", { method: method === "google" ? "google" : "email" });
};

/**
 * Alta fallida. `errorKey` es la clave i18n que devuelve `RegisterError`
 * (`errorEmailInUse`, `errorWeakPassword`, ...), no el texto traducido.
 */
export const trackRegisterFailed = (step: number, errorKey: string): void => {
  void trackError("register_failed", errorKey, "auth");
  void trackFeatureUsage(FEATURE, "register_failed", {
    step,
    error_key: errorKey,
  });
};

/**
 * Salida hacia el checkout de Stripe durante el alta.
 *
 * `planId` es el id de la licencia del catalogo, no un dato de la persona.
 */
export const trackPlanCheckoutStarted = (
  planId: string,
  quantity: number,
  isRetry: boolean,
): void => {
  void trackFeatureUsage(FEATURE, "plan_checkout_started", {
    plan_id: planId,
    quantity,
    is_retry: isRetry,
  });
};

export const trackPlanCheckoutFailed = (planId: string): void => {
  void trackFeatureUsage(FEATURE, "plan_checkout_failed", {
    plan_id: planId,
  });
};

/**
 * La empresa se creo pero el usuario se quedo sin pagar: ni reintento ni
 * redireccion a Stripe. Es la fuga mas cara del embudo de alta.
 */
export const trackPlanCheckoutAbandoned = (planId: string): void => {
  void trackFeatureUsage(FEATURE, "plan_checkout_abandoned", {
    plan_id: planId,
  });
};
